/**
 * useUserSettings — клиентские настройки юзера (persist в localStorage).
 *
 * Что хранится:
 *   - soundsEnabled: общий тумблер UI-звуков (uiTap, navTab, bell, plasma, …).
 *     Выключение сразу тишина; при включении — звуки возвращаются.
 *   - language: 'ru' | 'en'. Initial value — detected by i18n
 *     (Telegram language_code / navigator). При первом старте, если в
 *     localStorage нет settings, берём активный `i18n.language`.
 *
 * Уведомляет подписчиков через CustomEvent 'luna:settings-changed',
 * чтобы хуки useUISounds могли моментально подхватить toggle без
 * перемонтажа компонентов.
 */
import { useEffect, useState } from 'react';
import i18n from '@/i18n';
import { api } from '@/services/api';

export type UILanguage = 'ru' | 'en';

export interface UserSettings {
  soundsEnabled: boolean;
  loaderSoundEnabled: boolean;
  language: UILanguage;
}

const STORAGE_KEY = 'luna_user_settings_v1';
const EVENT_NAME = 'luna:settings-changed';

function detectedLanguage(): UILanguage {
  const lng = (i18n.language || '').toLowerCase();
  return lng === 'ru' ? 'ru' : 'en';
}

function defaultSettings(): UserSettings {
  return {
    soundsEnabled: true,
    loaderSoundEnabled: true,
    language: detectedLanguage(),
  };
}

function readFromStorage(): UserSettings {
  const defaults = defaultSettings();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<UserSettings>;
    return {
      soundsEnabled:
        typeof parsed.soundsEnabled === 'boolean'
          ? parsed.soundsEnabled
          : defaults.soundsEnabled,
      loaderSoundEnabled:
        typeof parsed.loaderSoundEnabled === 'boolean'
          ? parsed.loaderSoundEnabled
          : defaults.loaderSoundEnabled,
      language:
        parsed.language === 'en' || parsed.language === 'ru'
          ? parsed.language
          : defaults.language,
    };
  } catch {
    return defaults;
  }
}

let lastPushedLang: UILanguage | null = null;

function writeToStorage(settings: UserSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: settings }));
    // Синхронизация с i18next — мгновенно меняем активный язык UI.
    if (i18n.language !== settings.language) {
      void i18n.changeLanguage(settings.language);
    }
    // Синхронизация с бэком — пишем language_code в БД, чтобы push/TG
    // уведомления приходили на том же языке что и UI. Fire-and-forget,
    // дебаунсим по памяти чтобы не спамить при повторных записях.
    if (lastPushedLang !== settings.language) {
      lastPushedLang = settings.language;
      void api.post('/me/language', { language: settings.language }).catch(() => {
        // Сбросим чтобы ретраить при следующей записи
        lastPushedLang = null;
      });
    }
  } catch {
    /* silent — приватный режим браузера */
  }
}

/**
 * Sync-геттер для использования вне React-компонентов (например,
 * в модуле useUISounds при каждом play()).
 */
export function getUserSettings(): UserSettings {
  return readFromStorage();
}

export function useUserSettings(): [
  UserSettings,
  (patch: Partial<UserSettings>) => void,
] {
  const [settings, setSettings] = useState<UserSettings>(() => readFromStorage());

  useEffect(() => {
    const onChange = (e: Event) => {
      const ce = e as CustomEvent<UserSettings>;
      if (ce.detail) setSettings(ce.detail);
      else setSettings(readFromStorage());
    };
    window.addEventListener(EVENT_NAME, onChange);
    // Также слушаем 'storage' — переключение в другой вкладке.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSettings(readFromStorage());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const update = (patch: Partial<UserSettings>) => {
    const next = { ...readFromStorage(), ...patch };
    writeToStorage(next);
    setSettings(next);
  };

  return [settings, update];
}
